import { ArrowDown, ArrowUp, ArrowUpDown, Loader2 } from 'lucide-react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { cn } from '@/lib/utils';

import {
  type Column,
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type OnChangeFn,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';

interface DataTableProps<TData> {
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<TData, any>[];
  data: TData[];
  /** Controlled sorting state */
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  /** Shown when there are no rows */
  emptyState?: React.ReactNode;
  onRowClick?: (row: TData) => void;
  className?: string;
  isLoading?: boolean;
  headerClassName?: string;
}

export function DataTable<TData>({
  columns,
  data,
  sorting,
  onSortingChange,
  emptyState,
  onRowClick,
  className,
  isLoading,
  headerClassName,
}: DataTableProps<TData>) {
  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: false,
  });

  const buildTableBody = () => {
    if (isLoading) {
      return (
        <TableRow>
          <TableCell colSpan={columns.length} className="h-32 text-center">
            <div className="flex flex-col  items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span>Loading...</span>
            </div>
          </TableCell>
        </TableRow>
      );
    } else if (table.getRowModel().rows.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={columns.length} className="h-32 text-center">
            {emptyState ?? <span className="text-sm text-muted-foreground">No results found.</span>}
          </TableCell>
        </TableRow>
      );
    } else {
      return table.getRowModel().rows.map(row => (
        <TableRow
          key={row.id}
          data-state={row.getIsSelected() ? 'selected' : undefined}
          className={cn(onRowClick && 'cursor-pointer')}
          onClick={() => onRowClick?.(row.original)}
        >
          {row.getVisibleCells().map(cell => (
            <TableCell key={cell.id}>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          ))}
        </TableRow>
      ));
    }
  };

  return (
    <Table className={className}>
      <TableHeader className={headerClassName || 'bg-muted'}>
        {table.getHeaderGroups().map(headerGroup => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map(header => (
              <TableHead
                key={header.id}
                style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}
                className={cn(
                  header.column.getCanSort() &&
                    'cursor-pointer select-none hover:bg-muted/80 transition-colors'
                )}
                onClick={header.column.getToggleSortingHandler()}
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>

      <TableBody>{buildTableBody()}</TableBody>
    </Table>
  );
}

export function SortableHeader<TData, TValue>({
  column,
  label,
}: {
  column: Column<TData, TValue>;
  label: string;
}) {
  const sortDirection = column?.getIsSorted() || false;

  const Icon = !sortDirection ? ArrowUpDown : sortDirection === 'asc' ? ArrowUp : ArrowDown;
  return (
    <div
      className="flex items-center gap-2"
      onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
    >
      {label}
      <Icon className="h-4 w-4 opacity-50" />
    </div>
  );
}
